import { ADJUSTMENTS, adjustmentLutKey, packAdjustment } from '../adjustments';
import type { AdjustmentState } from '../core/types';
import type { AdjustmentPass } from '../gl/renderer';
import type { Texture } from '../gl/texture';
import type { Engine } from './engine';
import type { DocumentResources } from './resources';

/**
 * Scratch slots for the blurred copy Shadows/Highlights reads. Everything below
 * 8 belongs to the stroke pipeline and to `ops.ts`.
 */
const AUX_BLUR = 8;
const AUX_SCRATCH = 9;

/** Gaussian copy of `source`, which is how a local adjustment sees its surroundings. */
function blurredCopy(
  engine: Engine,
  resources: DocumentResources,
  source: Texture,
  radius: number,
): Texture {
  const blurred = resources.rgba(AUX_BLUR);
  const scratch = resources.rgba(AUX_SCRATCH);
  const sigma = Math.max(0.6, radius / 2);
  const taps = Math.max(1, Math.min(64, Math.ceil(radius * 1.5)));

  engine.renderer.blurColorPass(scratch.framebuffer, source, 1 / source.width, 0, taps, sigma);
  engine.renderer.blurColorPass(
    blurred.framebuffer,
    scratch.texture,
    0,
    1 / source.height,
    taps,
    sigma,
  );
  return blurred.texture;
}

/**
 * Everything the adjustment shader needs for one state: the uniform slots, the
 * lookup row where the adjustment is a table, and the blurred copy where it
 * looks at a neighbourhood.
 */
export function adjustmentPass(
  engine: Engine,
  resources: DocumentResources,
  state: AdjustmentState,
  source: Texture,
): AdjustmentPass {
  const definition = ADJUSTMENTS[state.id];
  const key = adjustmentLutKey(state);
  const lut = key && definition.lut ? engine.lutTexture(key, () => definition.lut!(state)) : null;

  const radius = definition.auxRadius?.(state) ?? 0;
  const aux = radius >= 1 ? blurredCopy(engine, resources, source, radius) : null;

  return { type: definition.type, params: packAdjustment(state), lut, aux };
}
